// NEW_FILE_JUSTIFIED: AI draft inline review component for conversation views

import { useState, useCallback } from 'react';
import { Bot, ExternalLink, Pencil, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { MESSAGE_CLASSIFICATIONS } from '@/@types/aiReply';
import type { IAiDraftBubbleProps } from '@/@types/aiReply';

export function AiDraftBubble({ draft, onSend, onCancel, onEditAndSend }: IAiDraftBubbleProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedText, setEditedText] = useState(draft.draftText);

  const classificationMeta = MESSAGE_CLASSIFICATIONS[draft.classification];

  const handleToggleEdit = useCallback(() => {
    setIsEditMode(prev => !prev);
  }, []);

  const handleSend = useCallback(async () => {
    await onSend(draft._id, editedText);
  }, [draft._id, editedText, onSend]);

  const handleCancel = useCallback(async () => {
    await onCancel(draft._id);
  }, [draft._id, onCancel]);

  const handleEditAndSend = useCallback(() => {
    if (onEditAndSend) {
      onEditAndSend(editedText);
    }
  }, [onEditAndSend, editedText]);

  return (
    <div className="flex justify-end max-w-[80%] w-fit ml-auto">
      <Card className="border-2 border-dashed border-blue-400 dark:border-blue-600 bg-card px-4 py-3 rounded-2xl rounded-br-sm shadow-none">
        {/* Header row */}
        <div className="flex items-center gap-2 mb-2">
          <Bot className="size-3.5 text-blue-500 shrink-0" aria-hidden="true" />
          <Badge className="text-xs bg-blue-600 hover:bg-blue-600 text-white border-0">
            AI Draft
          </Badge>
          {classificationMeta && (
            <Badge
              variant="outline"
              className="text-xs text-blue-600 dark:text-blue-400 border-blue-400 dark:border-blue-500 bg-transparent"
            >
              {classificationMeta.label}
            </Badge>
          )}
        </div>

        {/* Draft content */}
        {isEditMode ? (
          <Textarea
            value={editedText}
            onChange={e => setEditedText(e.target.value)}
            className="min-h-[80px] text-sm resize-none"
            aria-label="Edit AI draft text"
          />
        ) : (
          <p className="text-sm text-foreground leading-snug whitespace-pre-wrap">{editedText}</p>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleEdit}
            className={cn(
              'h-7 text-xs',
              isEditMode &&
                'bg-blue-50 dark:bg-blue-900/30 border-blue-400 text-blue-700 dark:text-blue-300'
            )}
            aria-pressed={isEditMode}
            aria-label={isEditMode ? 'Cancel editing' : 'Edit draft'}
          >
            <Pencil className="size-3 mr-1" aria-hidden="true" />
            {isEditMode ? 'Done' : 'Edit'}
          </Button>

          <Button
            size="sm"
            onClick={handleSend}
            className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            aria-label="Send AI draft"
          >
            <Send className="size-3 mr-1" aria-hidden="true" />
            Send
          </Button>

          {onEditAndSend && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditAndSend}
              className="h-7 text-xs border-blue-400 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30"
              aria-label="Edit draft in compose form and send"
            >
              <ExternalLink className="size-3 mr-1" aria-hidden="true" />
              Edit &amp; Send
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="h-7 text-xs ml-auto hover:border-destructive hover:text-destructive"
            aria-label="Cancel AI draft"
          >
            <X className="size-3 mr-1" aria-hidden="true" />
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}
